import React from 'react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { Flex, Box, Image, Button } from '@chatterbug/aaron'
import { t } from 'src/lib/i18n-react'
import { usePageParams } from 'src/utils/params'

import TutorAvatarPill from 'src/ui/TutorAvatarPill/TutorAvatarPill'
import routes from 'src/lib/rails-routes/rails-routes'
import { TYPES } from 'src/pages/Achievements/subcomponents/AchievementWidget/AchievementWidget'
import ActivityTimeInfo from 'src/ui/ActivityTimeInfo/ActivityTimeInfo'

import { ratingThumbsUp, ratingThumbsDown } from './assets'

const WidgetDetail = ({
  widgetType,
  tutorAvatarUrl,
  tutorLogin,
  tutorName,
  unitName,
  subTitle,
  date,
  duration,
  showPlaceholder = false,
  title,
  cefrLevel,
  ratingAgainForTutor,
  liveLessonNumber = null,
  // tutor isFavorite
  isFavorite,
  // TODO: put this into prop object?
  appointmentId,
  isList,
}) => {
  const getTitle = (type) => {
    if (liveLessonNumber) {
      return t('Lesson %{liveLessonNumber}', { liveLessonNumber })
    }
    if (type === TYPES.LIVE_LESSON) {
      return t('Your recent Live Lesson')
    }
    return title
  }

  const getPlaceholderText = (type) => {
    return (
      {
        liveLesson: {
          title: t('Your recent Live Lesson'),
          // would need doubble qutes here because of the haven`t
          text: t(
            `You haven't completed any Live Lessons yet. You will find the summary and recording here once you complete a lesson.`
          ),
          linkText: t(' Schedule a Live Lesson'),
          linkUrl: routes.advanced_schedule(l2Code),
          addition: t(` at any time.`),
          titleUrl: '#',
        },
        concept: {
          title: t('Concepts'),
          text: t(
            `You haven't studied any concepts yet. As you complete the curriculum we will suggest them for you.`
          ),
        },
        flashcards: {
          title: t('Flashcards'),
          text: t(
            `You haven't completed any flashcards yet. As you study words you'll see your progress and achievements here.`
          ),
        },
        writing: {
          title: t('Writing Practice'),
          text: t(
            `You haven't completed any writing yet. You can find writing practice under units in`
          ),
          linkText: t(` My Course.`),
          linkUrl: routes.advanced_my_course(l2Code),
        },
        media: {
          title: t('Media'),
          text: t(
            `You haven't completed any Media yet. You can find Media under units in`
          ),
          linkText: t(` My Course.`),
          linkUrl: routes.advanced_my_course(l2Code),
        },
      }[type] || ''
    )
  }

  const { l2Code } = usePageParams()
  const content = getPlaceholderText(widgetType)
  if (showPlaceholder) {
    return (
      <Flex
        justifyContent="flex-end"
        ml={{ _: 0, tabletWide: '2x' }}
        mt={{ _: '2x', tabletWide: 0 }}
      >
        <Box color="gray60">
          <span>{content?.text}</span>
          {content?.linkText && (
            <span>
              <Box
                as="a"
                href={content?.linkUrl}
                display="inline"
                color="blue42"
                textDecoration="none"
                fontWeight="600"
                sx={{
                  ':hover': {
                    color: 'blue42',
                  },
                }}
              >
                {content?.linkText}
              </Box>
            </span>
          )}
          {content?.addition && <span>{content?.addition}</span>}
        </Box>
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column">
      {content?.titleUrl ? (
        <Box
          as="h4"
          variant="text.h4"
          mt={
            isList || widgetType !== TYPES.LIVE_LESSON
              ? 0
              : { _: '2x', tablet: 0 }
          }
          sx={{
            cursor: 'pointer',
            color: 'black',
            ':hover': {
              color: 'blue42',
            },
            ':active': {
              color: 'blue30',
            },
          }}
          onClick={() => {
            window.location.href = content.titleUrl
          }}
        >
          {getTitle(widgetType)}
        </Box>
      ) : (
        <Box as="h4" variant="text.h4">
          {getTitle(widgetType)}
        </Box>
      )}
      {unitName && !isList && (
        <Box
          as="h5"
          variant="text.h5"
          mb={4}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {unitName}
        </Box>
      )}

      {/* check font size */}
      <Box mt={widgetType === TYPES.LIVE_LESSON ? '1x' : 0}>
        {widgetType === TYPES.FLASHCARDS && (
          <Box mb="1x" variant="text.h5">
            {t('Flashcard Study')}
          </Box>
        )}
        {cefrLevel ? (
          <Box variant="text.h6">
            {`${cefrLevel} • ${subTitle ? subTitle : t('Review')}`}
          </Box>
        ) : (
          <Box variant="text.h6">{t('Review')}</Box>
        )}
      </Box>
      {(tutorLogin || ratingAgainForTutor) && (
        <Flex mt="1x">
          {tutorLogin && (
            <TutorAvatarPill
              isFavorite={isFavorite}
              login={tutorLogin}
              name={tutorName}
              avatarUrl={tutorAvatarUrl}
            />
          )}
          {ratingAgainForTutor && (
            <>
              <Image
                ml="1x"
                src={
                  ratingAgainForTutor === 'positive'
                    ? ratingThumbsUp
                    : ratingThumbsDown
                }
                data-tip={
                  ratingAgainForTutor === 'positive'
                    ? t('You enjoyed this lesson')
                    : t('The lesson didn’t go well 😞')
                }
                data-for={`data-for-${ratingAgainForTutor}`}
              />
              <ReactTooltip
                id={`data-for-${ratingAgainForTutor}`}
                place="bottom"
                className="react-tooltip-small"
              />
            </>
          )}
        </Flex>
      )}
      <Flex flexDirection="row" justifyContent="space-between">
        <ActivityTimeInfo date={date} duration={duration} />
        {isList && !ratingAgainForTutor && (
          <Flex display={{ _: 'none', tablet: 'flex' }} color="blue42">
            <Box alignSelf="flex-end">
              <Button
                href={routes.chat_complete(appointmentId)}
                type="success"
                fill="outline"
                css={{
                  textDecoration: 'none',
                  // blue42
                  color: '#006FD6',
                }}
              >
                {t('rate tutor')}
              </Button>
            </Box>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

WidgetDetail.propTypes = {
  widgetType: PropTypes.string,
  tutorAvatarUrl: PropTypes.string,
  tutorLogin: PropTypes.string,
  tutorName: PropTypes.string,
  unitName: PropTypes.string,
  subTitle: PropTypes.string,
  date: PropTypes.string,
  duration: PropTypes.string,
  showPlaceholder: PropTypes.bool,
  title: PropTypes.string,
  cefrLevel: PropTypes.string,
  ratingAgainForTutor: PropTypes.oneOf(['positive', 'negative']),
  liveLessonNumber: PropTypes.number,
  isFavorite: PropTypes.bool,
  appointmentId: PropTypes.string,
  isList: PropTypes.bool,
}

export default WidgetDetail
